import { useLocation } from "react-router-dom";
import { useMemo } from "react";
import {
  getRouteInfo,
  getRouteDisplayName,
  generateBreadcrumbs,
  isRouteActive,
  getParentRoute,
  BreadcrumbItem,
  RouteInfo
} from "@/utils/routeMapping";

export interface CurrentRoute {
  /**
   * Information about the current route
   */
  current: RouteInfo | null;

  /**
   * Breadcrumb items for the current path
   */
  breadcrumbs: BreadcrumbItem[];

  /**
   * Parent route information (for nested routes)
   */
  parent?: RouteInfo;

  /**
   * Display name for the current route
   */
  displayName: string;

  /**
   * Current path string
   */
  path: string;

  /**
   * Check if a specific route is active
   */
  isActive: (routePath: string) => boolean;

  /**
   * Check if the current route is a nested route
   */
  isNested: boolean;
}

/**
 * Custom hook to get current route information
 * Provides comprehensive data about the current page including
 * route info, breadcrumbs, and active state checking
 *
 * @returns CurrentRoute object with route information
 */
export function useCurrentRoute(): CurrentRoute {
  const location = useLocation();
  const currentPath = location.pathname;

  const routeData = useMemo(() => {
    const current = getRouteInfo(currentPath);
    const breadcrumbs = generateBreadcrumbs(currentPath);
    const parent = getParentRoute(currentPath);
    const displayName = getRouteDisplayName(currentPath);
    const isNested = !!parent;

    return {
      current,
      breadcrumbs,
      parent,
      displayName,
      path: currentPath,
      isActive: (routePath: string) => isRouteActive(currentPath, routePath),
      isNested
    };
  }, [currentPath]);

  return routeData;
}